import { Task } from '@lit/task';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { api } from './api.js';

import { clientName, KNOWN_CLIENT_IDS } from '@flakiness/server/common/knownClientIds.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { TypedHTTP } from '@flakiness/shared/common/typedHttp.js';
import { ContextType } from '@lit/context';
import { SlDialog, SlInput } from '@shoelace-style/shoelace';
import Bowser from 'bowser';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { consume, contexts, FKUserChanged } from './contexts.js';
import { consumeOAuthError } from './oauthError.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { getInitials } from './utils.js';
import { tasks } from './tasks.js';

// Mirrors the `userLogin` schema in server/src/node/api/private.ts, so a bad login is
// caught on the field instead of coming back as an opaque 400.
const LOGIN_RE = /^[a-zA-Z0-9-._]+$/;
const LOGIN_MAX_LENGTH = 64;
// "Looks like an email" — the server validates strictly with z.string().email().
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// 1Password treats the profile fields as a login form and ignores
// autocomplete=off. Its opt-out is `data-1p-ignore`, which has to be on the real
// <input>; sl-input renders that in its shadow root and doesn't forward the attribute.
function ignorePasswordManager(element?: Element) {
  if (!element)
    return;
  const slInput = element as SlInput;
  slInput.updateComplete.then(() => slInput.input.setAttribute('data-1p-ignore', ''));
}

function formatVersion(version?: string): string|undefined {
  return version?.replace(/(?:\.0)+$/, '');
}

function formatPlatform(type?: string): string|undefined {
  if (type === 'mobile')
    return 'Mobile';
  if (type === 'tablet')
    return 'Tablet';
  if (type === 'desktop')
    return 'Desktop';
  return undefined;
}

function formatCountry(countryCode?: string): string|undefined {
  if (!countryCode)
    return undefined;
  if (countryCode === 'T1')
    return 'Tor network';
  if (countryCode === 'XX')
    return 'Unknown country';
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(countryCode) ?? countryCode;
  } catch {
    return countryCode;
  }
}

function formatLocation(location?: WireTypes.UserSession['lastLocation']): string|undefined {
  if (!location)
    return undefined;
  const parts = [location.city, location.region, formatCountry(location.countryCode)]
    .filter((part): part is string => !!part)
    .filter((part, index, all) => all.findIndex(candidate => candidate.toLowerCase() === part.toLowerCase()) === index);
  return parts.length ? parts.join(', ') : undefined;
}

function parseUserAgent(userAgent?: string) {
  if (!userAgent)
    return undefined;
  try {
    return Bowser.parse(userAgent);
  } catch {
    return undefined;
  }
}

function identityProviderName(provider: WireTypes.IdentityProvider): string {
  return provider === 'github' ? 'GitHub' : 'GitLab';
}

@customElement('page-user-settings')
export class PageUserSettings extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [ '/settings' ],
      render: (groups) => html`<page-user-settings></page-user-settings>`,
    }];
  }

  static url(): string {
    return platformUrl('/settings');
  }

  @consume({ context: contexts.user, subscribe: true })
  private _user?: ContextType<typeof contexts.user>;;

  @consume({ context: contexts.serverInfo, subscribe: true })
  private _serverInfo?: ContextType<typeof contexts.serverInfo>;;

  private _sessionsTask = new Task(this, {
    args: () => [this._user],
    task: async ([user], { signal }) => {
      const [currentSession, allSessions] = await Promise.all([
        api.user.currentSession.GET(),
        api.user.sessions.GET()
      ]);
      return { currentSession, allSessions };
    }
  });

  @query('#login') private _loginInput?: SlInput;
  @query('#name') private _nameInput?: SlInput;
  @query('#email') private _emailInput?: SlInput;
  @query('#unlink-account-dialog') private _unlinkAccountDialog?: SlDialog;
  @query('#delete-account-dialog') private _deleteAccountDialog?: SlDialog;

  // Gates the Save button. Recomputed on every keystroke rather than derived in
  // render(), since the inputs hold their own draft state.
  @state() private _profileChanged = false;
  @state() private _savingProfile = false;
  @state() private _refreshingAvatar = false;
  @state() private _profileStatus?: {
    success: boolean,
    message: string,
  };
  @state() private _accountError?: string;
  @state() private _unlinkProvider?: WireTypes.IdentityProvider;
  @state() private _unlinkingAccount = false;
  @state() private _deleteConfirmation = '';
  @state() private _deletingAccount = false;
  @state() private _deleteAccountError?: string;
  private _validateTimer?: ReturnType<typeof setTimeout>;

  override connectedCallback() {
    super.connectedCallback();
    this._accountError = consumeOAuthError();
  }

  private _profileDraft() {
    return {
      login: this._loginInput?.value.trim() ?? '',
      name: this._nameInput?.value.trim() ?? '',
      email: this._emailInput?.value.trim() ?? '',
    };
  }

  private _onProfileInput() {
    const draft = this._profileDraft();
    const loginValid = !!draft.login && draft.login.length <= LOGIN_MAX_LENGTH && LOGIN_RE.test(draft.login);
    const emailValid = !draft.email || EMAIL_RE.test(draft.email);
    const edited = !!this._user && (
      draft.login !== this._user.userLogin ||
      draft.name !== this._user.userName ||
      draft.email !== (this._user.email ?? '')
    );
    this._profileChanged = edited && loginValid && !!draft.name && emailValid;
    this._profileStatus = undefined;

    // Any keystroke supersedes a previous status message ("Saved", error, …).
    for (const input of [this._loginInput, this._nameInput, this._emailInput])
      input?.setCustomValidity('');
    // Re-arm the debounce: only complain once the user pauses, so a
    // half-typed address does not flash red on every character.
    if (this._validateTimer !== undefined)
      clearTimeout(this._validateTimer);
    this._validateTimer = setTimeout(() => {
      this._validateTimer = undefined;
      if (draft.login && !loginValid) {
        const message = `Use letters, numbers, dots, dashes and underscores, up to ${LOGIN_MAX_LENGTH} characters`;
        this._loginInput?.setCustomValidity(message);
        this._profileStatus = { success: false, message };
      } else if (draft.email && !emailValid) {
        const message = 'Please enter a valid email address';
        this._emailInput?.setCustomValidity(message);
        this._profileStatus = { success: false, message };
      }
    }, 500);
  }

  private async _saveProfile() {
    if (!this._profileChanged || this._savingProfile)
      return;
    const draft = this._profileDraft();
    this._savingProfile = true;
    this._profileStatus = undefined;
    try {
      await api.user.updateProfile.POST({
        login: draft.login,
        name: draft.name,
        email: draft.email || undefined,
      });
      this._profileChanged = false;
      this._profileStatus = { success: true, message: 'Your profile has been updated.' };
      // Refresh the user context so the rest of the app and the input values
      // reflect the saved profile.
      this.dispatchEvent(new FKUserChanged());
    } catch (e) {
      // A taken login is the one failure that belongs to a specific field.
      if (e instanceof TypedHTTP.HttpError && e.status === 409) {
        const message = `@${draft.login} is already taken`;
        this._loginInput?.setCustomValidity(message);
        this._profileStatus = { success: false, message };
      } else {
        this._profileStatus = { success: false, message: e instanceof Error ? e.message : 'Failed to update profile' };
      }
    } finally {
      this._savingProfile = false;
    }
  }

  private async _refreshAvatar() {
    if (this._refreshingAvatar)
      return;
    this._refreshingAvatar = true;
    this._profileStatus = undefined;
    try {
      await api.user.refreshAvatar.POST({});
      this._profileStatus = { success: true, message: 'Your profile photo has been refreshed.' };
      this.dispatchEvent(new FKUserChanged());
    } catch (e) {
      this._profileStatus = { success: false, message: e instanceof Error ? e.message : 'Failed to refresh profile photo' };
    } finally {
      this._refreshingAvatar = false;
    }
  }

  private async _showUnlinkDialog(provider: WireTypes.IdentityProvider) {
    this._accountError = undefined;
    this._unlinkProvider = provider;
    await this.updateComplete;
    await this._unlinkAccountDialog?.show();
  }

  private async _unlinkIdentity() {
    const provider = this._unlinkProvider;
    if (!provider || this._unlinkingAccount)
      return;
    this._unlinkingAccount = true;
    this._accountError = undefined;
    try {
      await api.user.unlinkIdentity.POST({ provider });
      this.dispatchEvent(new FKUserChanged());
    } catch (e) {
      this._accountError = e instanceof Error ? e.message : `Failed to disconnect ${identityProviderName(provider)}`;
    } finally {
      await this._unlinkAccountDialog?.hide();
      this._unlinkingAccount = false;
      this._unlinkProvider = undefined;
    }
  }

  private async _showDeleteAccountDialog() {
    this._deleteConfirmation = '';
    this._deleteAccountError = undefined;
    await this._deleteAccountDialog?.show();
  }

  private async _deleteAccount() {
    const user = this._user;
    if (!user || this._deletingAccount || this._deleteConfirmation !== user.userLogin)
      return;
    this._deletingAccount = true;
    this._deleteAccountError = undefined;
    try {
      await api.user.deleteAccount.POST({ login: this._deleteConfirmation });
      window.location.assign('/logout');
    } catch (error) {
      this._deleteAccountError = error instanceof Error ? error.message : 'Failed to delete account';
      this._deletingAccount = false;
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._validateTimer !== undefined) {
      clearTimeout(this._validateTimer);
      this._validateTimer = undefined;
    }
  }

  private _renderSession(session: WireTypes.UserSession, currentSession?: WireTypes.UserSession) {
    const current = session.sessionPublicId === currentSession?.sessionPublicId;
    const isCLI = session.clientId === KNOWN_CLIENT_IDS.OFFICIAL_CLI;
    const parsedUserAgent = parseUserAgent(session.lastUserAgent);
    const browserVersion = formatVersion(parsedUserAgent?.browser.version);
    const browser = parsedUserAgent?.browser.name ? [parsedUserAgent.browser.name, browserVersion].filter(Boolean).join(' ') : undefined;
    const platform = formatPlatform(parsedUserAgent?.platform.type);
    const device = [platform, parsedUserAgent?.os.name].filter(Boolean).join(' · ');
    const location = formatLocation(session.lastLocation);
    const title = isCLI ? session.name ?? clientName(session.clientId) : browser ?? session.name ?? clientName(session.clientId);
    const description = isCLI ? clientName(session.clientId) : device;
    const icon = isCLI ? 'bx-terminal' :
      parsedUserAgent?.platform.type === 'mobile' || parsedUserAgent?.platform.type === 'tablet' ? 'bx-mobile-alt' :
      parsedUserAgent?.platform.type === 'desktop' ? 'bx-desktop' : 'bx-devices';
    const lastAccessDate = new Date(session.lastAccessTimestampSeconds * 1000);

    return html`
      <article class=session>
        <div class=session-icon>
          <sl-icon library=boxicons name=${icon}></sl-icon>
        </div>
        <div class=session-content>
          <div class=session-heading>
            <span class=session-title>${title}</span>
            ${current ? html`<sl-tag size=small pill variant=success>Current</sl-tag>` : nothing}
          </div>
          <div class=session-facts>
            ${description ? html`<span>${description}</span>` : nothing}
            ${location ? html`
              <span class=location>
                <sl-icon library=boxicons name=bx-map-pin></sl-icon>
                ${location}
              </span>
            ` : nothing}
          </div>
          <div class=session-last-access title=${lastAccessDate.toLocaleString()}>
            Last active <sl-relative-time date=${lastAccessDate.toISOString()}></sl-relative-time>
          </div>
        </div>
        <sl-button size=small @click=${async () => {
          await api.user.logoutSession.POST({
            sessionId: session.sessionPublicId,
          });
          this._sessionsTask.run();
        }}>Logout</sl-button>
      </article>
    `;
  }

  private _renderSocialAccount(provider: WireTypes.IdentityProvider) {
    const name = identityProviderName(provider);
    const enabled = provider === 'github' ? this._serverInfo?.enabledGithub : this._serverInfo?.enabledGitlab;
    const linked = this._user?.linkedProviders.includes(provider);
    if (!enabled && !linked)
      return nothing;
    const canUnlink = linked && this._user!.linkedProviders.length > 1;
    return html`
      <article class=account>
        <div class="account-icon ${provider}">
          <sl-icon name=${provider}></sl-icon>
        </div>
        <div class=account-content>
          <div class=account-heading>
            <span class=account-name>${name}</span>
            ${linked ? html`<sl-tag size=small pill variant=success>Connected</sl-tag>` : nothing}
          </div>
          <div class=account-description>
            ${linked
              ? canUnlink
                ? `You can use this account to sign in to Flakiness.`
                : `This is your only sign-in method. Connect another account before disconnecting it.`
              : `Connect your ${name} account as another way to sign in.`}
          </div>
        </div>
        ${linked ? html`
          <sl-button
            size=small
            variant=danger
            ?disabled=${!canUnlink}
            title=${canUnlink ? `Disconnect ${name}` : 'Connect another account before disconnecting this one'}
            @click=${() => this._showUnlinkDialog(provider)}
          >Disconnect</sl-button>
        ` : html`
          <sl-button
            size=small
            variant=primary
            href=${`/login/${provider}/link?return_to=${encodeURIComponent(window.location.href)}`}
          >
            <sl-icon slot=prefix name=link-45deg></sl-icon>
            Connect
          </sl-button>
        `}
      </article>
    `;
  }

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [],
    metadata: () => ({ title: 'Settings · Dashboard' }),
  });

  render() {
    const unlinkProviderName = this._unlinkProvider ? identityProviderName(this._unlinkProvider) : '';
    return html`
      <user-header submenu=settings></user-header>
      <app-body>
        <sl-dialog
          id=unlink-account-dialog
          label=${`Disconnect ${unlinkProviderName}?`}
          @sl-after-hide=${() => {
            if (!this._unlinkingAccount)
              this._unlinkProvider = undefined;
          }}
        >
          <p>
            ${unlinkProviderName} will no longer be available as a way to sign in.
            You can connect it again later.
          </p>
          <sl-button
            slot=footer
            ?disabled=${this._unlinkingAccount}
            @click=${() => this._unlinkAccountDialog?.hide()}
          >Cancel</sl-button>
          <sl-button
            slot=footer
            variant=danger
            ?loading=${this._unlinkingAccount}
            @click=${() => this._unlinkIdentity()}
          >Disconnect</sl-button>
        </sl-dialog>

        ${this._user ? html`
          <sl-dialog
            id=delete-account-dialog
            label="Delete your account?"
            @sl-after-hide=${() => {
              if (!this._deletingAccount) {
                this._deleteConfirmation = '';
                this._deleteAccountError = undefined;
              }
            }}
          >
            <p>
              This permanently deletes your profile and signs out every device.
              Organizations and projects will not be deleted.
            </p>
            <p>
              If you own any organizations, transfer their ownership or delete
              them before deleting your account.
            </p>
            ${this._deleteAccountError ? html`
              <sl-alert class=delete-account-error variant=danger open>
                ${this._deleteAccountError}
              </sl-alert>
            ` : nothing}
            <sl-input
              label=${`Type ${this._user.userLogin} to confirm`}
              autocomplete=off
              ${ref(ignorePasswordManager)}
              autofocus
              .value=${this._deleteConfirmation}
              ?disabled=${this._deletingAccount}
              @sl-input=${(event: Event) => this._deleteConfirmation = (event.target as SlInput).value}
            ></sl-input>
            <sl-button
              slot=footer
              ?disabled=${this._deletingAccount}
              @click=${() => this._deleteAccountDialog?.hide()}
            >Cancel</sl-button>
            <sl-button
              slot=footer
              variant=danger
              ?loading=${this._deletingAccount}
              ?disabled=${this._deleteConfirmation !== this._user.userLogin}
              @click=${() => this._deleteAccount()}
            >Delete account</sl-button>
          </sl-dialog>
        ` : nothing}

        <div class=page-heading>
          <h1>Settings</h1>
          <p>Manage your profile, connected accounts, and signed-in devices.</p>
        </div>

        ${this._user ? html`
          <form-section label="Profile">
            <div slot=description>Choose how you appear across Flakiness.</div>
            <section class="settings-card profile">
              <aside class=profile-photo>
                <div class=avatar-frame>
                  <sl-avatar
                    image=${this._user.avatarUrl ?? ''}
                    initials=${getInitials(this._user.userName)}
                    label=${this._user.userName}
                  ></sl-avatar>
                </div>
                <div class=photo-copy>
                  <div class=photo-label>Profile photo</div>
                  <div class=photo-help>Managed by your sign-in provider</div>
                </div>
                ${this._serverInfo?.enabledGithub && !this._serverInfo.isSingleUserMode && this._user.linkedProviders.includes('github') ? html`
                  <sl-button
                    class=refresh-avatar
                    size=small
                    ?loading=${this._refreshingAvatar}
                    @click=${() => this._refreshAvatar()}
                  ><sl-icon slot=prefix name=arrow-clockwise></sl-icon>Refresh</sl-button>
                ` : nothing}
              </aside>

              <div class=profile-editor>
                <div class=profile-fields>
                  <sl-input
                    id=login
                    label=Login
                    placeholder=login
                    autocomplete=off
                    ${ref(ignorePasswordManager)}
                    .value=${this._user.userLogin}
                    ?disabled=${this._savingProfile}
                    @sl-input=${() => this._onProfileInput()}
                  ><span slot=prefix>@</span></sl-input>

                  <sl-input
                    id=name
                    label="Display name"
                    placeholder="Your name"
                    autocomplete=off
                    ${ref(ignorePasswordManager)}
                    .value=${this._user.userName}
                    ?disabled=${this._savingProfile}
                    @sl-input=${() => this._onProfileInput()}
                  ></sl-input>

                  <sl-input
                    class=email-field
                    id=email
                    label=Email
                    type=email
                    placeholder="you@company.com"
                    autocomplete=off
                    ${ref(ignorePasswordManager)}
                    .value=${this._user.email ?? ''}
                    ?disabled=${this._savingProfile}
                    @sl-input=${() => this._onProfileInput()}
                  ><sl-icon slot=prefix name=envelope></sl-icon></sl-input>
                </div>

                <footer class=profile-actions>
                  <div class=profile-status aria-live=polite>
                    ${this._profileStatus ? html`
                      <sl-icon name=${this._profileStatus.success ? 'check-circle-fill' : 'exclamation-triangle-fill'}></sl-icon>
                      <span>${this._profileStatus.message}</span>
                    ` : nothing}
                  </div>
                  <sl-button
                    variant=primary
                    ?loading=${this._savingProfile}
                    ?disabled=${!this._profileChanged}
                    @click=${() => this._saveProfile()}
                  >Save changes</sl-button>
                </footer>
              </div>
            </section>
          </form-section>
        ` : nothing}

        ${this._user && this._serverInfo && !this._serverInfo.isSingleUserMode && (this._user.linkedProviders.length > 0 || this._serverInfo.enabledGithub || this._serverInfo.enabledGitlab) ? html`
          <form-section label="Connected accounts">
            <div slot=description>Connect another account as an additional way to sign in.</div>
            <div class=accounts-stack>
              ${this._accountError ? html`
                <sl-alert class=account-error variant=danger open closable @sl-after-hide=${() => this._accountError = undefined}>
                  ${this._accountError}
                </sl-alert>
              ` : nothing}
              <section class="settings-card accounts">
                ${this._renderSocialAccount('github')}
                ${this._renderSocialAccount('gitlab')}
              </section>
            </div>
          </form-section>
        ` : nothing}

        <form-section label="Active sessions">
          <div slot=description>Review the browsers and devices currently signed in to your account.</div>
          <section class="settings-card sessions">
            ${this._sessionsTask.render({
              initial: () => html`
                <div class=sessions-loading role=status>
                  <sl-spinner></sl-spinner>
                  <span>Loading active sessions…</span>
                </div>
              `,
              pending: () => html`
                <div class=sessions-loading role=status>
                  <sl-spinner></sl-spinner>
                  <span>Loading active sessions…</span>
                </div>
              `,
              error: () => html`<div class=sessions-error>Failed to load active sessions.</div>`,
              complete: ({ currentSession, allSessions }) => allSessions
                .toSorted((a, b) => {
                  const aIsCurrent = a.sessionPublicId === currentSession.sessionPublicId;
                  const bIsCurrent = b.sessionPublicId === currentSession.sessionPublicId;
                  return Number(bIsCurrent) - Number(aIsCurrent) || b.lastAccessTimestampSeconds - a.lastAccessTimestampSeconds;
                })
                .map(session => this._renderSession(session, currentSession)),
            })}
          </section>
        </form-section>

        ${this._user && this._serverInfo && !this._serverInfo.isSingleUserMode ? html`
          <form-section label="Delete account">
            <div slot=description>Permanently remove your Flakiness profile.</div>
            <section class="settings-card delete-account">
              <div class=delete-account-copy>
                <strong>Delete your account and sign out everywhere</strong>
                <p>Your organizations and their project data will not be deleted.</p>
              </div>
              <sl-button variant=danger @click=${() => this._showDeleteAccountDialog()}>Delete account</sl-button>
            </section>
          </form-section>
        ` : nothing}
      </app-body>
      <app-footer></app-footer>
    `;
  }

  static styles = [linkStyles, pageStyles, css`
    .page-heading h1 {
      margin-bottom: var(--sl-spacing-2x-small);
    }

    .page-heading p {
      margin: 0;
      color: var(--sl-color-neutral-500);
    }

    .settings-card {
      border: 1px solid var(--fk-color-border);
      border-radius: var(--sl-border-radius-large);
      background: var(--sl-color-neutral-0);
      box-shadow: var(--sl-shadow-x-small);
      overflow: hidden;
    }

    .profile {
      display: grid;
      grid-template-columns: 13rem minmax(0, 1fr);
    }

    .profile-photo {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--sl-spacing-medium);
      min-width: 0;
      padding: var(--sl-spacing-x-large);
      border-right: 1px solid var(--fk-color-border);
      background: var(--sl-color-neutral-50);
      text-align: center;
    }

    .avatar-frame {
      display: grid;
      place-items: center;
      width: 7rem;
      height: 7rem;
      border: 1px solid var(--fk-color-border);
      border-radius: 50%;
      background: var(--sl-color-neutral-0);
      box-shadow: var(--sl-shadow-small);
    }

    .profile-photo sl-avatar {
      --size: 6rem;
    }

    .photo-label {
      color: var(--sl-color-neutral-800);
      font-weight: var(--sl-font-weight-semibold);
    }

    .photo-help {
      margin-top: var(--sl-spacing-2x-small);
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-x-small);
      line-height: var(--sl-line-height-dense);
    }

    .refresh-avatar {
      margin-top: var(--sl-spacing-x-small);
    }

    .profile-editor {
      min-width: 0;
      padding: var(--sl-spacing-x-large);
    }

    .profile-fields {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      min-width: 0;
      gap: var(--sl-spacing-medium);
    }

    .profile-fields sl-input {
      width: 100%;
      min-width: 0;
    }

    .profile-fields sl-input[data-user-invalid]::part(base) {
      border-color: var(--sl-color-danger-600);
    }

    .profile-fields sl-input:focus-within[data-user-invalid]::part(base) {
      border-color: var(--sl-color-danger-600);
      box-shadow: 0 0 0 var(--sl-focus-ring-width) var(--sl-color-danger-300);
    }

    .email-field {
      grid-column: 1 / -1;
    }

    .profile-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--sl-spacing-medium);
      min-height: var(--sl-input-height-medium);
      margin-top: var(--sl-spacing-large);
      padding-top: var(--sl-spacing-large);
      border-top: 1px solid var(--fk-color-border);
    }

    .profile-status {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-x-small);
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);

      &:has(sl-icon[name=check-circle-fill]) {
        color: var(--sl-color-success-600);
      }

      &:has(sl-icon[name=exclamation-triangle-fill]) {
        color: var(--sl-color-danger-600);
      }
    }

    .sessions {
      background: var(--sl-color-neutral-0);
    }

    .sessions-loading,
    .sessions-error {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--sl-spacing-small);
      min-height: 7rem;
      padding: var(--sl-spacing-large);
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);
    }

    .sessions-loading sl-spinner {
      font-size: var(--sl-font-size-x-large);
    }

    .sessions-error {
      color: var(--sl-color-danger-600);
    }

    .delete-account {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--sl-spacing-large);
      padding: var(--sl-spacing-large);
      border-color: var(--sl-color-danger-300);
    }

    .delete-account-copy strong {
      color: var(--sl-color-danger-700);
    }

    .delete-account-copy p {
      margin: var(--sl-spacing-2x-small) 0 0;
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);
    }

    .delete-account-error {
      display: block;
      margin: var(--sl-spacing-medium) 0;
    }

    .accounts-stack {
      display: grid;
      gap: var(--sl-spacing-medium);
    }

    .account-error::part(base) {
      border-color: var(--sl-color-danger-400);
      background: var(--sl-color-danger-100);
      color: var(--sl-color-danger-900);
    }

    .account {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      gap: var(--sl-spacing-medium);
      align-items: center;
      padding: var(--sl-spacing-medium);

      &:not(:last-child) {
        border-bottom: 1px solid var(--fk-color-border);
      }
    }

    .account-icon {
      display: grid;
      place-items: center;
      width: 2.75rem;
      height: 2.75rem;
      border-radius: var(--sl-border-radius-medium);
      background: var(--sl-color-neutral-100);
      color: var(--sl-color-neutral-800);

      &.gitlab {
        color: #e24329;
        background: #fff4ef;
      }

      sl-icon {
        font-size: 1.5rem;
      }
    }

    .account-content {
      min-width: 0;
    }

    .account-heading {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--sl-spacing-x-small);
    }

    .account-name {
      font-weight: var(--sl-font-weight-semibold);
    }

    .account-description {
      margin-top: 2px;
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);
    }

    .session {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      gap: var(--sl-spacing-medium);
      align-items: center;
      padding: var(--sl-spacing-medium);

      &:not(:last-child) {
        border-bottom: 1px solid var(--fk-color-border);
      }

      &:hover {
        background: var(--fk-color-tablerow-hover);
      }
    }

    .session-icon {
      display: grid;
      place-items: center;
      width: 2.75rem;
      height: 2.75rem;
      border-radius: var(--sl-border-radius-medium);
      color: var(--sl-color-neutral-700);
      background: var(--sl-color-neutral-100);
      flex-shrink: 0;

      sl-icon {
        font-size: 1.5rem;
      }
    }

    .session-content {
      min-width: 0;
    }

    .session-heading {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--sl-spacing-x-small);
    }

    .session-title {
      font-weight: var(--sl-font-weight-semibold);
    }

    .session-facts {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--sl-spacing-x-small) var(--sl-spacing-small);
      margin-top: 2px;
      color: var(--sl-color-neutral-600);
      font-size: var(--sl-font-size-small);
    }

    .location {
      display: inline-flex;
      align-items: center;
      gap: 3px;

      sl-icon {
        font-size: 1em;
      }
    }

    .session-last-access {
      margin-top: var(--sl-spacing-x-small);
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-x-small);
    }

    @media (max-width: 600px) {
      .profile {
        grid-template-columns: minmax(0, 1fr);
      }

      .profile-photo {
        flex-direction: row;
        justify-content: flex-start;
        padding: var(--sl-spacing-large);
        border-right: 0;
        border-bottom: 1px solid var(--fk-color-border);
        text-align: left;
      }

      .refresh-avatar {
        margin-top: 0;
        margin-left: auto;
      }

      .avatar-frame {
        width: 5rem;
        height: 5rem;
        flex-shrink: 0;
      }

      .profile-photo sl-avatar {
        --size: 4.25rem;
      }

      .profile-editor {
        padding: var(--sl-spacing-large);
      }

      .profile-fields {
        grid-template-columns: minmax(0, 1fr);
      }

      .email-field {
        grid-column: auto;
      }

      .profile-actions {
        align-items: stretch;
        flex-direction: column;
      }

      .profile-actions sl-button {
        width: 100%;
      }

      .session {
        grid-template-columns: auto minmax(0, 1fr);
      }

      .account {
        grid-template-columns: auto minmax(0, 1fr);
      }

      .account > sl-button {
        grid-column: 2;
        justify-self: start;
      }

      .session > sl-button {
        grid-column: 2;
        justify-self: start;
      }

      .delete-account {
        align-items: stretch;
        flex-direction: column;
      }
    }
  `];
}
